// Theme JavaScript for Verified by Strand
// Progressive enhancement: everything works without JS via normal form posts.

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Smooth scroll (Lenis) ---
  let lenis = null;
  if (!reducedMotion && typeof Lenis !== 'undefined') {
    lenis = new Lenis({ lerp: 0.1, duration: 1.2, smoothWheel: true });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }

  // --- Scroll reveal with stagger ---
  document.querySelectorAll('[data-reveal-group]').forEach((group) => {
    group.querySelectorAll('[data-reveal]').forEach((el, i) => {
      el.style.setProperty('--reveal-delay', `${Math.min(i * 90, 540)}ms`);
    });
  });
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));

  // --- Hero mobile carousel (cross-fade + progress bars) ---
  const heroCarousel = document.querySelector('[data-hero-carousel]');
  if (heroCarousel) {
    const slides = Array.from(heroCarousel.querySelectorAll('[data-hero-slide]'));
    const bars = Array.from(document.querySelectorAll('[data-hero-bar]'));
    if (slides.length > 1) {
      let idx = 0;
      let timer = null;
      const show = (i) => {
        idx = (i + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle('is-active', n === idx));
        bars.forEach((b, n) => b.classList.toggle('is-active', n === idx));
      };
      const stop = () => {
        if (timer) { clearInterval(timer); timer = null; }
      };
      const mq = window.matchMedia('(max-width: 1023px)');
      const start = () => {
        stop();
        if (reducedMotion || !mq.matches || document.hidden) return;
        timer = setInterval(() => show(idx + 1), 5000);
      };
      bars.forEach((b, n) => b.addEventListener('click', () => { show(n); start(); }));
      // Swipe
      let startX = null;
      heroCarousel.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; stop(); }, { passive: true });
      heroCarousel.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) show(idx + (dx < 0 ? 1 : -1));
        startX = null;
        start();
      }, { passive: true });
      mq.addEventListener('change', start);
      document.addEventListener('visibilitychange', start);
      show(0);
      start();
    }
  }

  // --- Header: solidify (white + dark logo) once scrolled past the top ---
  const transparentHeader = document.querySelector('[data-header][data-transparent]');
  if (transparentHeader) {
    const onHeaderScroll = () => {
      transparentHeader.classList.toggle('is-scrolled', window.scrollY > 40);
    };
    onHeaderScroll();
    window.addEventListener('scroll', onHeaderScroll, { passive: true });
  }

  // --- Read more (mobile only): reveal toggle when clamped text overflows ---
  document.querySelectorAll('[data-readmore]').forEach((wrap) => {
    const text = wrap.querySelector('[data-readmore-text]');
    const btn = wrap.querySelector('[data-readmore-toggle]');
    if (!text || !btn) return;
    const refresh = () => {
      const clamped = !text.classList.contains('line-clamp-none');
      const overflows = text.scrollHeight - text.clientHeight > 4;
      btn.classList.toggle('hidden', !(clamped && overflows));
    };
    btn.addEventListener('click', () => {
      const expanded = text.classList.toggle('line-clamp-none');
      btn.textContent = expanded ? 'Visa mindre' : 'Läs mer';
      if (!expanded) refresh();
    });
    refresh();
    window.addEventListener('resize', refresh);
  });

  // --- Product image gallery (click a thumbnail to swap the main image) ---
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const mains = Array.from(gallery.querySelectorAll('[data-gallery-main]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (thumbs.length < 2) return;
    const select = (i) => {
      mains.forEach((m) => {
        const active = m.dataset.galleryMain === String(i);
        m.classList.toggle('opacity-100', active);
        m.classList.toggle('opacity-0', !active);
        m.classList.toggle('pointer-events-none', !active);
      });
      thumbs.forEach((t) => {
        const active = t.dataset.galleryThumb === String(i);
        t.classList.toggle('ring-[#00272C]', active);
        t.classList.toggle('ring-transparent', !active);
      });
    };
    thumbs.forEach((t) => t.addEventListener('click', () => select(Number(t.dataset.galleryThumb))));
  });

  // --- Bildgalleri: klick på en bild öppnar den i helskärm, pilar och piltangenter bläddrar ---
  document.querySelectorAll('[data-image-gallery]').forEach((section) => {
    const dialog = section.querySelector('[data-gallery-dialog]');
    const items = Array.from(section.querySelectorAll('[data-gallery-open]'));
    if (!dialog || !items.length || typeof dialog.showModal !== 'function') return;
    const img = dialog.querySelector('[data-gallery-dialog-img]');
    const caption = dialog.querySelector('[data-gallery-dialog-caption]');
    let current = 0;
    const show = (i) => {
      current = (i + items.length) % items.length;
      const item = items[current];
      img.src = item.dataset.full;
      img.alt = item.dataset.alt || '';
      caption.textContent = item.dataset.caption || '';
    };
    items.forEach((item, i) =>
      item.addEventListener('click', () => {
        show(i);
        dialog.showModal();
        lenis?.stop();
      })
    );
    dialog.addEventListener('close', () => lenis?.start());
    dialog.querySelector('[data-gallery-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-gallery-prev]').addEventListener('click', () => show(current - 1));
    dialog.querySelector('[data-gallery-next]').addEventListener('click', () => show(current + 1));
    // Klick bredvid bilden stänger
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target === img.parentElement) dialog.close();
    });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(current - 1);
      if (e.key === 'ArrowRight') show(current + 1);
    });
    if (items.length < 2) {
      dialog.querySelector('[data-gallery-prev]').hidden = true;
      dialog.querySelector('[data-gallery-next]').hidden = true;
    }
  });

  // --- Quote-only products: keep the "Begär offert" link in sync with the chosen variant ---
  document.querySelectorAll('[data-quote-box]').forEach((box) => {
    const select = box.querySelector('[data-quote-variant]');
    const link = box.querySelector('[data-quote-link]');
    if (!select || !link) return;
    const base = link.href.split('?')[0];
    const sync = () => {
      link.href = `${base}?product=${encodeURIComponent(`${box.dataset.quoteProduct} – ${select.value}`)}`;
    };
    select.addEventListener('change', sync);
    sync();
  });

  // --- Offertformulär: fyll i maskinen när kunden kommer från en "Begär offert"-knapp ---
  const quoteProduct = new URLSearchParams(window.location.search).get('product');
  if (quoteProduct) {
    const quoteBody = document.querySelector('textarea[name="contact[body]"]');
    if (quoteBody && !quoteBody.value.trim()) {
      quoteBody.value = `Hej! Jag vill gärna ha en offert på: ${quoteProduct}\n\n`;
    }
    // Offertformuläret har ett eget maskinfält som annars är dolt
    document.querySelectorAll('[data-quote-product-field]').forEach((field) => {
      field.value = quoteProduct;
      const row = field.closest('[data-quote-product-row]');
      if (row) row.hidden = false;
    });
  }

  // --- Offert- och kontaktformulär till kundportalen (CRM) ---
  // Skickas samtidigt som Shopifys vanliga formulär, via meta-capi/api/crm-lead.js som
  // håller portalens nyckel. Ett gemensamt händelse-id i det dolda fältet vbs_event_id
  // gör att Meta-pixeln och CRM-leadet går att koppla ihop när Kristofer sätter Vunnen.
  const CRM_URL = 'https://vbs-meta-capi.vercel.app/api/crm-lead';
  const newEventId = () => Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  const readCookie = (name) => (document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)')) || [])[1] || '';
  // Shopifys samtyckes-API laddas inte av sig själv. Utan det ser varje besökare ut att
  // ha nekat marknadsföring, och då skickas ingen annonsdata (och ingen återkoppling).
  let marketingAllowed = () => false;
  try {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) marketingAllowed = () => Boolean(window.Shopify?.customerPrivacy?.marketingAllowed?.());
    });
  } catch (e) {}
  document.querySelectorAll('form[action*="/contact"]').forEach((form) => {
    const idField = document.createElement('input');
    idField.type = 'hidden';
    idField.name = 'vbs_event_id'; // utanför contact[...], så det hamnar inte i mejlet
    idField.value = newEventId();
    form.appendChild(idField);

    form.addEventListener('submit', () => {
      const data = new FormData(form);
      const get = (name) => String(data.get(name) || '').trim();
      const skip = new Set(['contact[name]', 'contact[email]', 'contact[phone]', 'contact[body]']);
      const answers = {};
      data.forEach((value, key) => {
        const m = key.match(/^contact\[(.+)\]$/);
        if (m && !skip.has(key) && String(value).trim()) answers[m[1]] = String(value).trim();
      });
      if (get('contact[body]')) answers['Meddelande'] = get('contact[body]');
      const params = new URLSearchParams(window.location.search);
      let consent = false;
      try {
        consent = marketingAllowed();
      } catch (e) {}
      const payload = {
        name: get('contact[name]'),
        email: get('contact[email]'),
        phone: get('contact[phone]'),
        product: get('contact[Gäller produkt]'),
        answers,
        page: window.location.origin + window.location.pathname,
        eventId: idField.value,
        consent,
        fbp: consent ? readCookie('_fbp') : '',
        fbc: consent ? readCookie('_fbc') : '',
        utm_source: params.get('utm_source') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || '',
      };
      if (!payload.name || (!payload.email && !payload.phone)) return;
      const body = JSON.stringify(payload);
      // sendBeacon överlever att sidan byts direkt efter submit
      if (!(navigator.sendBeacon && navigator.sendBeacon(CRM_URL, new Blob([body], { type: 'text/plain' })))) {
        fetch(CRM_URL, { method: 'POST', body, keepalive: true }).catch(() => {});
      }
    });
  });

  // --- Fast offertknapp i mobilen: syns efter första sektionen, döljs vid formuläret och sidfoten ---
  const stickyCta = document.querySelector('[data-sticky-cta]');
  if (stickyCta) {
    const targetId = (stickyCta.dataset.stickyCtaTarget || '').split('#')[1];
    const target = targetId ? document.getElementById(targetId) : null;
    const footer = document.querySelector('.shopify-section-group-footer-group');
    const firstSection = document.querySelector('main > .shopify-section');
    const inView = new Set();
    const updateStickyCta = () => {
      const pastFirst = firstSection
        ? firstSection.getBoundingClientRect().bottom < 0
        : window.scrollY > 400;
      stickyCta.classList.toggle('is-visible', pastFirst && inView.size === 0);
    };
    const stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) inView.add(entry.target);
        else inView.delete(entry.target);
      });
      updateStickyCta();
    });
    [target, footer].forEach((el) => el && stickyObserver.observe(el));
    window.addEventListener('scroll', updateStickyCta, { passive: true });
    updateStickyCta();
  }

  // --- Priser inkl./exkl. moms: CSS döljer fel pris, här synkas knappar och variantlistor ---
  const applyVat = (mode) => {
    document.documentElement.dataset.vat = mode;
    document.querySelectorAll('[data-vat-set]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.vatSet === mode));
    });
    document.querySelectorAll('option[data-vat-text-incl]').forEach((opt) => {
      opt.textContent = mode === 'excl' ? opt.dataset.vatTextExcl : opt.dataset.vatTextIncl;
    });
  };
  const currentVat = () => (document.documentElement.dataset.vat === 'excl' ? 'excl' : 'incl');
  const saveVat = (mode) => {
    try {
      localStorage.setItem('vat', mode);
    } catch (err) {}
  };
  applyVat(currentVat());

  // --- Popup vid första besöket: privatperson (inkl. moms) eller företag (exkl. moms) ---
  const vatPopup = document.querySelector('[data-vat-popup]');
  const hasVatChoice = () => {
    try {
      return localStorage.getItem('vat') !== null;
    } catch (err) {
      return true; // Utan localStorage går valet inte att spara, så fråga inte på varje sida
    }
  };
  const closeVatPopup = () => {
    if (!vatPopup || vatPopup.classList.contains('hidden')) return;
    vatPopup.classList.add('hidden');
    document.body.style.overflow = '';
    lenis?.start();
    // Stängd utan val: behåll nuvarande läge och fråga inte igen
    saveVat(currentVat());
  };
  if (vatPopup && !hasVatChoice()) {
    setTimeout(() => {
      if (hasVatChoice()) return;
      vatPopup.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
      lenis?.stop();
    }, 800);
  }

  // --- Kollektionsfilter: visa/dölj filterpanelen ---
  document.querySelectorAll('[data-filter-toggle]').forEach((toggle) => {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    // Öppen från start när kunden redan filtrerat, så valen syns
    if (panel.querySelector('input:checked, input[type="number"][value]:not([value=""])')) {
      panel.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }
    toggle.addEventListener('click', () => {
      const open = panel.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
  });

  // --- Product recommendations (async via Shopify Recommendations API) ---
  document.querySelectorAll('[data-product-recommendations]').forEach((el) => {
    const url = el.dataset.url;
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-product-recommendations]');
        if (!fresh || !fresh.innerHTML.trim().length) return;
        el.innerHTML = fresh.innerHTML;
        // Re-arm staggered scroll reveals for the injected cards
        el.querySelectorAll('[data-reveal-group]').forEach((group) => {
          group.querySelectorAll('[data-reveal]').forEach((node, i) => {
            node.style.setProperty('--reveal-delay', `${Math.min(i * 90, 540)}ms`);
          });
        });
        el.querySelectorAll('[data-reveal]').forEach((node) => io.observe(node));
      })
      .catch(() => {});
  });

  const drawerSection = () => document.getElementById('shopify-section-cart-drawer');
  const drawer = () => document.getElementById('CartDrawer');

  function openDrawer() {
    const el = drawer();
    if (!el) return;
    el.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    lenis?.stop();
  }

  function closeDrawer() {
    const el = drawer();
    if (!el) return;
    el.classList.add('hidden');
    document.body.style.overflow = '';
    lenis?.start();
  }

  function isDrawerOpen() {
    const el = drawer();
    return el && !el.classList.contains('hidden');
  }

  // Re-render the drawer section + cart count badge from the server
  async function refreshCart() {
    const wasOpen = isDrawerOpen();
    const [sectionRes, cartRes] = await Promise.all([
      fetch('/?sections=cart-drawer'),
      fetch('/cart.js'),
    ]);
    if (sectionRes.ok) {
      const sections = await sectionRes.json();
      const holder = drawerSection();
      if (holder && sections['cart-drawer']) {
        holder.innerHTML = sections['cart-drawer'];
        if (wasOpen) openDrawer();
      }
    }
    if (cartRes.ok) {
      const cart = await cartRes.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
    }
  }

  // Click handling: open/close drawer, search toggle, VAT toggle, remove line
  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-close]') || e.target.matches('[data-cart-overlay]')) {
      closeDrawer();
      return;
    }
    const searchToggle = e.target.closest('[data-search-toggle]');
    const menuToggle = e.target.closest('[data-menu-toggle]');
    if (searchToggle || menuToggle) {
      // Mobilmeny och sökfält delar plats under headern: öppna det ena, stäng det andra.
      // Headern blir solid (is-open) medan något av dem är öppet, så texten syns över heron.
      const openId = searchToggle ? 'HeaderSearch' : 'HeaderMenu';
      const panels = ['HeaderSearch', 'HeaderMenu'].map((id) => document.getElementById(id));
      panels.forEach((panel) => {
        if (!panel) return;
        const open = panel.id === openId ? panel.classList.contains('hidden') : false;
        panel.classList.toggle('hidden', !open);
        document
          .querySelectorAll(`[aria-controls="${panel.id}"]`)
          .forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
        if (open && panel.id === 'HeaderSearch') panel.querySelector('input[name="q"]')?.focus();
      });
      const anyOpen = panels.some((panel) => panel && !panel.classList.contains('hidden'));
      document.querySelector('[data-header]')?.classList.toggle('is-open', anyOpen);
      return;
    }
    const vatButton = e.target.closest('[data-vat-set], [data-vat-choose]');
    if (vatButton) {
      const mode = vatButton.dataset.vatSet || vatButton.dataset.vatChoose;
      applyVat(mode);
      saveVat(mode);
      closeVatPopup();
      return;
    }
    if (e.target.closest('[data-vat-popup-close]')) {
      closeVatPopup();
      return;
    }
    const removeLink = e.target.closest('[data-line-remove]');
    if (removeLink) {
      e.preventDefault();
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(removeLink.dataset.lineRemove), quantity: 0 }),
      });
      refreshCart();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      closeVatPopup();
    }
  });

  // Drawer quantity changes
  document.addEventListener('change', async (e) => {
    const qty = e.target.closest('[data-line-qty]');
    if (qty) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(qty.dataset.lineQty), quantity: Number(qty.value) }),
      });
      refreshCart();
      return;
    }
    // Cart page: auto-submit quantity updates
    if (e.target.matches('input[name="updates[]"]')) {
      e.target.closest('form')?.submit();
    }
  });

  // AJAX add to cart: open the drawer instead of redirecting to /cart
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
    if (res.ok) {
      await refreshCart();
      openDrawer();
    } else {
      form.submit();
    }
  });
})();
